import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import "./Header.scss";
import Permissions from "common/security/permissions";
import IAppStore from "./IAppStore";
import MerchantPicker from "features/login/MerchantPicker";
import Help from "./Help";
import useFeatureFlag from "features/featureFlags/useFeatureFlag";
import usePermission from "common/security/usePermission";
import NotificationCenter from "features/notifications/NotificationCenter";
import { Dropdown } from "react-bootstrap";
import NavbarMenuToggle from "common/ui/NavbarMenuToggle";
import { useSelector } from "react-redux";
import { useOktaAuth } from "@okta/okta-react";
import { localizeCommon as localize } from "features/localization/localizeService";
import Merchant, { MerchantFeature, isFeatureEnabled } from "domain/merchant";
import LocalizationSelector from "features/localization/localizationSelector";

declare var $: any;

const Header: React.FC<any> = () => {

    const isCustomer = useSelector((m: IAppStore) => m.userState.isCustomer);
    const isLoggedIn = useSelector((m: IAppStore) => m.userState.isLoggedIn);
    const name = useSelector((m: IAppStore) => m.userState.name);
    const activeMerchant = useSelector((m: IAppStore) => m.userState.activeMerchant);
    const merchants = useSelector((m: IAppStore) => m.userState.merchants);

    const showBidEditor = usePermission(Permissions.UpdateBid);
    const showReports = usePermission(Permissions.ViewReport);
    const showPositions = usePermission(Permissions.ViewPosition);

    const { oktaAuth } = useOktaAuth();

    const nonProdHeader = !useFeatureFlag("ShowProdHeader");
    const showTranslations = useFeatureFlag("ShowTranslations");

    const isHedgeRecordsAsLandingPage = activeMerchant ? isFeatureEnabled(activeMerchant, MerchantFeature.HedgeRecordsAsLandingPage) : false;

    function logout() {
        oktaAuth.signOut({clearTokensBeforeRedirect: true})
    }


    return <HeaderTemplate 
        name={name}
        isCustomer={isCustomer}
        activeMerchant={activeMerchant}
        merchants={merchants}
        nonProdHeader={nonProdHeader}
        showTranslations={showTranslations}
        dashboardPath={isHedgeRecordsAsLandingPage ? "/dashboard" : "/"}
        isLoggedIn={isLoggedIn}
        showBidEditor={showBidEditor}
        showReports={showReports}
        showPositions={showPositions}
        onLogout={logout}
        showNotificationCenter
        />;
};

export interface HeaderTemplateProps {
    name: string;
    isCustomer: boolean;
    activeMerchant: Merchant;
    merchants: Merchant[];
    nonProdHeader: boolean;
    showTranslations: boolean;
    dashboardPath: string;
    isLoggedIn: boolean;
    showBidEditor: boolean;
    showReports: boolean;
    showPositions: boolean;
    onLogout(): void;
    showNotificationCenter: boolean;
}

export const HeaderTemplate: React.FC<HeaderTemplateProps> = React.memo(props => {
    
    const env: string = process.env.REACT_APP_UI_ENV;

    return <header>
        <nav className="navbar navbar-expand-lg navbar-light scrolling-navbar header-height header-margin">
            <div>
                <button className="navbar-toggler" data-toggle="collapse" data-target="#navbarSupportedContent">
                    <span className="navbar-toggler-icon"></span>
                </button>
                <div className="navbar-brand">
                    <div className="header-brand">
                        <a href="/">
                            <img className="header-logo"
                                src="/images/stonex-hedge.svg"
                                alt="StoneX Hedge"
                            />
                        </a>
                        {props.isCustomer && <span className="header-merchant">{props.activeMerchant?.name}</span>}
                        {props.nonProdHeader && env !== 'Prod' && <div className="d-none d-md-inline header-site-name">{env}</div>}
                    </div>
                </div>
            </div>
            <div className="order-xs-3 order-md-1">
                <ul className="nav navbar-nav ml-auto icons-right">
                    {props.merchants?.length > 1 &&
                        <li className="nav-item">
                            <MerchantPicker
                                merchants={props.merchants}
                                activeMerchant={props.activeMerchant} />
                        </li>
                    }
                    {props.isLoggedIn && <>
                        <li className="nav-item">
                            <Help
                                merchantName={props.activeMerchant?.name}
                                supportEmail={props.activeMerchant?.email}
                                supportPhone={props.activeMerchant?.phone}
                                supportWebsiteUrl={props.activeMerchant?.websiteUrl}
                                isCustomer={props.isCustomer} />
                        </li>
                        <li className="nav-item">
                            {props.showNotificationCenter && <NotificationCenter />}
                        </li>
                        <li className="nav-item">
                            <Dropdown>
                                <Dropdown.Toggle as={NavbarMenuToggle} id="user-toggle">
                                    <i className="material-icons">account_circle</i>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Header>
                                        <h6>{localize("Header_Label_Welcome", { name: props.name })}</h6>
                                    </Dropdown.Header>
                                    <Dropdown.Divider />
                                    {props.showTranslations &&
                                            <>
                                                <Dropdown.Header>
                                                    <LocalizationSelector />
                                                </Dropdown.Header>
                                                <Dropdown.Divider />
                                            </>
                                        }
                                    <Dropdown.Item>
                                        <button className="link-button" onClick={() => props.onLogout()}>{localize("Header_Button_Logout")}</button>
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </li>
                    </>}
                </ul>
            </div>
            <div className="collapse navbar-collapse order-xs-2" id="navbarSupportedContent">
                {props.isLoggedIn && props.activeMerchant != null &&
                    <>
                        <ul className="navbar-nav top-nav nav nav-tabs" role="tablist">
                            <li className="nav-item">
                                <NavLink className="nav-link" to={props.dashboardPath} exact onClick={() => closeMenu()}>
                                    {localize("Header_Nav_Overview")}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink className="nav-link" to="/accounts" onClick={() => closeMenu()}>
                                    {localize("Header_Nav_Accounts")}
                                </NavLink>
                            </li>
                            {props.showPositions && <li className="nav-item">
                                <NavLink className="nav-link" to="/positions" onClick={() => closeMenu()}>
                                    {localize("Header_Nav_Positions")}
                                </NavLink>
                            </li>}
                            <li className="nav-item">
                                <NavLink className="nav-link" to="/bids" onClick={() => closeMenu()}>
                                    {localize("Header_Nav_Bids")}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink className="nav-link" to="/offers" onClick={() => closeMenu()}>
                                    {localize("Header_Nav_OffersAndContracts")}
                                </NavLink>
                            </li>
                            {!props.isCustomer &&
                                <li className="nav-item">
                                    <NavLink className="nav-link" to="/settings" onClick={() => closeMenu()}>
                                        {localize("Header_Nav_Settings")}
                                    </NavLink>
                                </li>
                            }
                            {props.showBidEditor &&
                                <li className="nav-item">
                                    <NavLink className="nav-link" to="/bid-editor" onClick={() => closeMenu()}>
                                        {localize("Header_Nav_BidEditor")}
                                    </NavLink>
                                </li>
                            }
                            {props.showReports &&
                                <li className="nav-item">
                                    <NavLink className="nav-link" to="/reports" onClick={() => closeMenu()}>
                                        {localize("Header_Nav_Reports")}
                                    </NavLink>
                                </li>
                            }
                        </ul>
                    </>}
            </div>

        </nav>
    </header>
})

function closeMenu() {
    $("#navbarSupportedContent").toggleClass("show", false);
}

export default Header;
